import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { AreaChart, Area, ResponsiveContainer, BarChart, Bar, LineChart, Line } from 'recharts';
import {
  Button,
  Flex,
  PageSection,
  Pagination,
} from '@patternfly/react-core';
import {
  EllipsisVIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InfoCircleIcon,
} from '@patternfly/react-icons';
import { PageContextActions, PageHeaderTitle } from '@app/components/PageContextActions';
import {
  Activity, ChevronRight,
  Cpu, MoreHorizontal, Plus, TrendingDown, TrendingUp, Zap,
} from 'lucide-react';
import { SeverityLabel } from '@app/components/SeverityLabel';
import { StatusLabel } from '@app/components/StatusLabel';
import { useTheme } from '@app/utils/theme';
import { ACM_DARK_TOKENS, ACM_LIGHT_TOKENS, AcmOverviewTokens } from './acmOverviewTokens';
import {
  ALERTS,
  CONNS,
  DOTS,
  KPIS,
  KpiItem,
  SECURITY_FEED_LEGEND,
  SECURITY_FEED_ROWS,
  SECURITY_FEED_TOTAL,
  SECURITY_SYSTEMS_LEGEND,
  SECURITY_SYSTEMS_TOTAL,
  arcD,
  polar,
  rStatus,
  rTotal,
  regions,
  sweepArc,
} from './acmOverviewData';
import './ACMOpenShiftOverview.css';

function WorldMap({ C }: { C: AcmOverviewTokens }) {
  const [hov, setHov] = useState<string | null>(null);
  const [pin, setPin] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const raf = useRef<number>();

  useEffect(() => {
    let t = 0;
    const loop = () => {
      t += 1;
      setTick(t);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current!);
  }, []);

  const active = hov ?? pin;
  const ar = regions.find((r) => r.id === active);
  const dash = -(tick * 0.28) % 18;
  const brand = C.info;
  const dotFill = `color-mix(in srgb, ${brand} 28%, transparent)`;
  const dotFillActive = `color-mix(in srgb, ${brand} 12%, transparent)`;
  const nodeFill = C.s2;
  const gridStroke = `color-mix(in srgb, ${brand} 8%, transparent)`;
  const connIdle = `color-mix(in srgb, ${brand} 18%, transparent)`;
  const connLit = brand;
  const connGlow = `color-mix(in srgb, ${brand} 40%, transparent)`;

  return (
    <div className="acm-overview__map" style={{ position: 'relative', width: '100%', height: 252, userSelect: 'none' }}>
      <svg viewBox="0 0 660 206" style={{ width: '100%', height: '100%' }} preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="acm-fglow">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {[52, 104, 156].map((y) => (
          <line key={y} x1="0" y1={y} x2="660" y2={y} stroke={gridStroke} strokeWidth="1" />
        ))}
        {DOTS.map((d, i) => (
          <circle key={i} cx={d.cx} cy={d.cy} r={1.4} fill={active ? dotFillActive : dotFill} />
        ))}
        {CONNS.map(([a, b], i) => {
          const ra = regions[a];
          const rb = regions[b];
          const lit = active === ra.id || active === rb.id;
          return (
            <g key={i}>
              {lit && (
                <path
                  d={arcD(ra.cx, ra.cy, rb.cx, rb.cy)}
                  fill="none"
                  stroke={connGlow}
                  strokeWidth={3.5}
                  filter="url(#acm-fglow)"
                  strokeDasharray="5 4"
                  strokeDashoffset={dash}
                />
              )}
              <path
                d={arcD(ra.cx, ra.cy, rb.cx, rb.cy)}
                fill="none"
                stroke={lit ? connLit : connIdle}
                strokeWidth={lit ? 0.9 : 0.6}
                strokeDasharray="5 4"
                strokeDashoffset={dash}
              />
            </g>
          );
        })}
        {regions.map((r, idx) => {
          const isA = active === r.id;
          const isP = pin === r.id;
          const st = rStatus(r, C);
          const tot = rTotal(r);
          const onPct = r.devices.online / tot;
          const dgPct = r.devices.degraded / tot;
          const oR = isA ? 16 : 12;
          const halo = oR + 5 + Math.sin(tick * 0.04 + idx * 1.2) * 2.5;
          const dgStartDeg = onPct * 360;
          const dgEndDeg = (onPct + dgPct) * 360;
          const dgS = polar(r.cx, r.cy, oR, dgStartDeg);
          const dgE = polar(r.cx, r.cy, oR, dgEndDeg);

          return (
            <g
              key={r.id}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHov(r.id)}
              onMouseLeave={() => setHov(null)}
              onClick={() => setPin((p) => (p === r.id ? null : r.id))}
            >
              <circle cx={r.cx} cy={r.cy} r={halo} fill="none" stroke={st.color} strokeWidth={0.6} opacity={isA ? 0.28 : 0.1} />
              <circle cx={r.cx} cy={r.cy} r={oR} fill={nodeFill} stroke={C.border2} strokeWidth={0.8} />
              {sweepArc(r.cx, r.cy, oR, onPct) && (
                <path d={sweepArc(r.cx, r.cy, oR, onPct)!} fill="none" stroke={C.success} strokeWidth={isA ? 2.2 : 1.8} strokeLinecap="round" filter={isA ? 'url(#acm-fglow)' : undefined} />
              )}
              {dgPct > 0.015 && (
                <path
                  d={`M${dgS.x} ${dgS.y} A${oR} ${oR} 0 ${dgPct > 0.5 ? 1 : 0} 1 ${dgE.x} ${dgE.y}`}
                  fill="none"
                  stroke={C.warning}
                  strokeWidth={isA ? 1.8 : 1.4}
                  strokeLinecap="round"
                />
              )}
              <circle cx={r.cx} cy={r.cy} r={isA ? 3.5 : 2.5} fill={isP ? C.red : st.color} />
              <text x={r.cx} y={r.cy - oR - 5} textAnchor="middle" fill={isA ? C.t1 : C.t3} fontSize={isA ? 9 : 8} fontWeight="600" letterSpacing=".06em" style={{ pointerEvents: 'none' }}>
                {r.id}
              </text>
              <text x={r.cx} y={r.cy + oR + 9} textAnchor="middle" fill={isA ? C.t2 : C.t4} fontSize={isA ? 8.5 : 7.5} fontWeight="500" style={{ pointerEvents: 'none' }}>
                {tot.toLocaleString()}
              </text>
            </g>
          );
        })}
      </svg>

      {ar && (() => {
        const st = rStatus(ar, C);
        const tot = rTotal(ar);
        return (
          <div className="acm-overview__map-tooltip" style={{ position: 'absolute', top: 16, right: 20, width: 208 }}>
            <div className="acm-overview__map-tooltip-accent" aria-hidden />
            <div className="acm-overview__map-tooltip-content">
              <div style={{ padding: '16px 16px 14px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="acm-overview__widget-title">{ar.label}</span>
                  <span
                    className="acm-overview__caption"
                    style={{
                      fontWeight: 500,
                      padding: '2px 6px',
                      borderRadius: 6,
                      color: ar.trend < 0 ? C.red : C.t1,
                      background: ar.trend < 0 ? C.redSoft : 'color-mix(in srgb, var(--pf-t--global--icon--color--regular) 8%, transparent)',
                    }}
                  >
                    {ar.trend > 0 ? '+' : ''}{ar.trend}%
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: st.color }} />
                  <span className="acm-overview__caption">{tot.toLocaleString()} devices · {ar.jobs} jobs</span>
                </div>
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { l: 'Online', n: ar.devices.online, c: C.success },
                  { l: 'Degraded', n: ar.devices.degraded, c: C.warning },
                  { l: 'Offline', n: ar.devices.offline, c: C.red },
                ].map((row) => (
                  <div key={row.l}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: row.c }} />
                        <span className="acm-overview__legend">{row.l}</span>
                      </div>
                      <span className="acm-overview__caption">
                        {row.n.toLocaleString()}
                        <span style={{ color: C.t4, fontWeight: 400 }}> · {Math.round(row.n / tot * 100)}%</span>
                      </span>
                    </div>
                    <div style={{ height: 2, borderRadius: 999, background: 'color-mix(in srgb, var(--pf-t--global--icon--color--regular) 12%, transparent)' }}>
                      <div style={{ height: '100%', borderRadius: 999, width: `${row.n / tot * 100}%`, background: row.c }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const KPI_ICONS: Record<string, typeof Cpu> = {
  'Total Devices': Cpu,
  'Total Jobs': Zap,
  'New devices': Plus,
};

function KpiCard({ k, C }: { k: KpiItem; C: AcmOverviewTokens }) {
  const [open, setOpen] = useState(false);
  const Icon = KPI_ICONS[k.label] ?? Activity;
  const isActionCard = Boolean(k.footerAction);
  const max = k.rows.length ? Math.max(...k.rows.map((r) => r.v)) : 1;
  const trackBg = 'color-mix(in srgb, var(--pf-t--global--icon--color--regular) 12%, transparent)';

  return (
    <div
      className={`acm-overview__kpi-card${open ? ' acm-overview__kpi-card--open' : ''}${isActionCard ? ' acm-overview__kpi-card--static' : ''}`}
      onClick={isActionCard ? undefined : () => setOpen((o) => !o)}
    >
      <div className="acm-overview__kpi-top">
        <Icon size={13} className="acm-overview__kpi-icon" />
        {k.badge ? (
          <StatusLabel status="danger">{k.badge}</StatusLabel>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: !k.up ? C.red : C.t1 }}>
            {k.up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            <span className="acm-overview__kpi-trend">{k.pct}</span>
          </div>
        )}
      </div>
      <div className="acm-overview__kpi-value">{k.value}</div>
      <div className="acm-overview__kpi-sub">{k.sub}</div>
      {isActionCard ? (
        <>
          <div style={{ height: 36, margin: '0 -4px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={k.chartData}>
                <Line type="monotone" dataKey="v" stroke={C.success} strokeWidth={1.4} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="acm-overview__kpi-label">
            <span>{k.footerAction}</span>
            <ChevronRight size={9} />
          </div>
        </>
      ) : (
        <>
          <div style={{ height: 36, margin: '0 -4px' }}>
            <ResponsiveContainer width="100%" height="100%">
              {k.chartType === 'stackedArea' ? (
                <AreaChart data={k.chartData}>
                  <Area type="monotone" dataKey="online" stackId="1" stroke={C.success} strokeWidth={1.1} fill={C.successSoft} dot={false} />
                  <Area type="monotone" dataKey="degraded" stackId="1" stroke={C.warning} strokeWidth={1.1} fill={C.warningSoft} dot={false} />
                  <Area type="monotone" dataKey="offline" stackId="1" stroke={C.red} strokeWidth={1.1} fill={C.redSoft} dot={false} />
                </AreaChart>
              ) : k.chartType === 'bar' ? (
                <BarChart data={k.chartData} barGap={1} barSize={3}>
                  <Bar dataKey="running" fill={C.info} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="failed" fill={C.red} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="queued" fill="var(--pf-t--global--icon--color--subtle)" radius={[2, 2, 0, 0]} />
                </BarChart>
              ) : k.chartType === 'stackedBar' ? (
                <BarChart data={k.chartData} barGap={0.5} barSize={4}>
                  <Bar dataKey="timeout" stackId="a" fill={C.warning} />
                  <Bar dataKey="error" stackId="a" fill={C.red} />
                  <Bar dataKey="canceled" stackId="a" fill="var(--pf-t--global--icon--color--subtle)" radius={[2, 2, 0, 0]} />
                </BarChart>
              ) : (
                <LineChart data={k.chartData}>
                  <Line type="monotone" dataKey="v" stroke={C.info} strokeWidth={1.4} dot={false} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
          {open && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {k.rows.map((row) => {
                const rowColor =
                  row.l === 'Online'
                    ? C.success
                    : row.l === 'Running' || row.l === 'Available'
                    ? C.info
                    : row.l === 'Degraded' || row.l === 'Timeout'
                      ? C.warning
                      : row.l === 'Offline' || row.l === 'Error' || row.l === 'Failed'
                        ? C.red
                        : 'var(--pf-t--global--icon--color--subtle)';
                return (
                <div key={row.l}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span className="acm-overview__kpi-row-label">{row.l}</span>
                    <span className="acm-overview__kpi-row-value">{row.v > 100 ? row.v.toLocaleString() : row.v}</span>
                  </div>
                  <div style={{ height: 2, borderRadius: 999, background: trackBg }}>
                    <div style={{ height: '100%', borderRadius: 999, width: `${(row.v / max) * 100}%`, background: rowColor }} />
                  </div>
                </div>
                );
              })}
            </div>
          )}
          <div className="acm-overview__kpi-label">
            <span>{k.label}</span>
            <ChevronRight size={9} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
          </div>
        </>
      )}
    </div>
  );
}

function SecurityDonut({
  items,
  total,
  label,
}: {
  items: { key: string; label: string; color: string; count: number }[];
  total: number;
  label: string;
}) {
  const size = 160;
  const stroke = 10;
  const gap = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="acm-overview__security-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${total.toLocaleString()} ${label}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--acm-border, rgba(0, 0, 0, 0.08))"
          strokeWidth={stroke}
        />
        {items.map((item) => {
          const pct = item.count / total;
          const dash = pct * c;
          const visible = Math.max(0, dash - gap);
          const el = (
            <circle
              key={item.key}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={item.color}
              strokeWidth={stroke}
              strokeDasharray={`${visible} ${c - visible}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              strokeLinecap="butt"
            >
              <title>{`${item.label}: ${item.count.toLocaleString()}`}</title>
            </circle>
          );
          offset += dash;
          return el;
        })}
      </svg>
      <div className="acm-overview__security-donut-center">
        <span className="acm-overview__security-donut-count">{total.toLocaleString()}</span>
        <span className="acm-overview__security-donut-label">{label}</span>
      </div>
    </div>
  );
}

function SecurityChart({
  items,
  total,
  label,
}: {
  items: { key: string; label: string; color: string; count: number }[];
  total: number;
  label: string;
}) {
  return (
    <div className="acm-overview__security-chart">
      <SecurityDonut items={items} total={total} label={label} />
      <div className="acm-overview__security-legend" role="list" aria-label={`${label} legend`}>
        {items.map((item) => (
          <div key={item.key} className="acm-overview__security-legend-item" role="listitem">
            <span className="acm-overview__security-legend-swatch" style={{ background: item.color }} />
            <span>{item.label}</span>
            <span className="acm-overview__security-legend-count">{item.count.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SecurityFeedWidget() {
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(20);

  return (
    <div className="acm-overview__security-feed">
      <div className="acm-overview__security-feed-title">Security feed</div>

      <div className="acm-overview__security-charts">
        <SecurityChart
          label="Total CVEs"
          total={SECURITY_FEED_TOTAL}
          items={SECURITY_FEED_LEGEND.map((item) => ({
            key: item.severity,
            label: item.label,
            color: item.color,
            count: item.count,
          }))}
        />
        <SecurityChart
          label="Total systems"
          total={SECURITY_SYSTEMS_TOTAL}
          items={SECURITY_SYSTEMS_LEGEND.map((item) => ({
            key: item.key,
            label: item.label,
            color: item.color,
            count: item.count,
          }))}
        />
      </div>

      <div className="acm-overview__security-table-panel">
        <div className="acm-overview__security-table" role="table" aria-label="Security feed">
          <div className="acm-overview__security-table-header" role="row">
            <div role="columnheader">CVE</div>
            <div role="columnheader">Severity</div>
            <div role="columnheader">Advisory</div>
            <div role="columnheader">Systems affected</div>
            <div role="columnheader" className="acm-overview__security-table-actions" />
          </div>

          {SECURITY_FEED_ROWS.map((row) => {
            return (
              <div key={row.id} className="acm-overview__security-table-row" role="row">
                <div role="cell" data-label="CVE">
                  <button type="button" className="acm-overview__security-link">
                    {row.cve}
                  </button>
                </div>
                <div role="cell" data-label="Severity">
                  <SeverityLabel severity={row.severity} />
                </div>
                <div role="cell" data-label="Advisory">
                  <button type="button" className="acm-overview__security-link">
                    {row.advisory}
                  </button>
                </div>
                <div role="cell" data-label="Systems affected">{row.affected}</div>
                <div role="cell" className="acm-overview__security-table-actions app-data-table__control app-data-table__control--actions">
                  <Button variant="plain" aria-label={`Actions for ${row.cve}`}>
                    <EllipsisVIcon />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="acm-overview__security-pagination">
          <Pagination
            itemCount={SECURITY_FEED_TOTAL}
            page={page}
            perPage={perPage}
            titles={{ items: 'CVEs' }}
            onSetPage={(_e, p) => setPage(p)}
            onPerPageSelect={(_e, next) => {
              setPerPage(next);
              setPage(1);
            }}
            widgetId="security-feed-pagination"
            variant="bottom"
            isCompact
            isStatic
          />
        </div>
      </div>
    </div>
  );
}

function ActivityWidget() {
  return (
    <div className="acm-overview__activity">
      <div className="acm-overview__activity-header">
        <span className="acm-overview__widget-title">Activities</span>
        <span className="acm-overview__caption">Last 5h</span>
      </div>
      <ul className="acm-overview__activity-list" aria-label="Activities">
        {ALERTS.map((a) => {
          const tone = a.lvl === 'critical' ? 'danger' : a.lvl === 'warning' ? 'warning' : 'info';
          const Icon =
            tone === 'danger' ? ExclamationCircleIcon : tone === 'warning' ? ExclamationTriangleIcon : InfoCircleIcon;
          return (
            <li key={a.id} className={`acm-overview__activity-item acm-overview__activity-item--${tone}`}>
              <div className="acm-overview__activity-heading">
                <Icon className="acm-overview__activity-icon" />
                <span className="acm-overview__activity-title">{a.title}</span>
                <span className="acm-overview__activity-time">{a.time}</span>
              </div>
              <p className="acm-overview__activity-msg">{a.msg}</p>
              <p className="acm-overview__activity-detail">{a.detail}</p>
              <p className="acm-overview__activity-systems">{a.systems} systems affected</p>
              <Button variant="link" isInline className="acm-overview__activity-action">
                Take action
              </Button>
            </li>
          );
        })}
      </ul>
      <div className="acm-overview__activity-footer">
        <Button variant="link" isInline>
          View all
        </Button>
      </div>
    </div>
  );
}

function OverviewCard({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={`acm-overview__card${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </div>
  );
}

const ACMOpenShiftOverview: React.FunctionComponent = () => {
  const { isDark } = useTheme();
  const C = isDark ? ACM_DARK_TOKENS : ACM_LIGHT_TOKENS;

  const overviewStyle = React.useMemo(
    () =>
      ({
        '--acm-bg': C.bg,
        '--acm-surface': C.s1,
        '--acm-surface-elevated': C.s2,
        '--acm-border': C.border,
        '--acm-border-strong': C.border2,
        '--acm-text-primary': C.t1,
        '--acm-text-secondary': C.t2,
        '--acm-text-muted': C.t3,
        '--acm-text-subtle': C.t4,
        '--acm-red': C.red,
        '--acm-warning': C.warning,
        '--acm-info': C.info,
        '--acm-info-soft': C.infoSoft,
        '--acm-success': C.success,
        '--acm-overview-gap': '12px',
      }) as React.CSSProperties,
    [C]
  );

  const globalOnline = regions.reduce((s, r) => s + r.devices.online, 0);
  const globalDegraded = regions.reduce((s, r) => s + r.devices.degraded, 0);
  const globalOffline = regions.reduce((s, r) => s + r.devices.offline, 0);
  const globalTotal = globalOnline + globalDegraded + globalOffline;

  return (
    <>
      <PageSection hasBodyWrapper={false} className="acm-page__header-section">
        <div className="acm-page__header-inner">
          <Flex
            justifyContent={{ default: 'justifyContentSpaceBetween' }}
            alignItems={{ default: 'alignItemsCenter' }}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
          >
            <PageHeaderTitle
              title="Overview"
              description="Fleet health, security, alerts, and tasks."
            />
            <PageContextActions placement="toolbar" />
          </Flex>
        </div>
      </PageSection>

      <PageSection hasBodyWrapper={false} className="acm-page__content-section">
        <div className="acm-overview" style={overviewStyle}>
          <div className="acm-overview__grid">
            {KPIS.map((k) => <KpiCard key={k.label} k={k} C={C} />)}

            <OverviewCard className="acm-overview__card--activity acm-overview__span-rows-3">
              <ActivityWidget />
            </OverviewCard>

            <OverviewCard className="acm-overview__span-3">
              <div className="acm-overview__map-layout">
                <div className="acm-overview__map-summary">
                  <div className="acm-overview__map-stat">
                    <span className="acm-overview__widget-title">Global Status</span>
                  </div>
                  <div className="acm-overview__map-stat">
                    <div className="acm-overview__metric-value">{globalTotal.toLocaleString()}</div>
                    <span className="acm-overview__caption">Total Devices</span>
                  </div>
                  <div className="acm-overview__map-stats">
                    {[
                      { label: 'Online', count: globalOnline, color: C.success },
                      { label: 'Degraded', count: globalDegraded, color: C.warning },
                      { label: 'Offline', count: globalOffline, color: C.red },
                    ].map((item) => {
                      const pct = Math.round((item.count / globalTotal) * 100);
                      return (
                        <div key={item.label} className="acm-overview__map-stat-row">
                          <div className="acm-overview__map-stat-row-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: item.color }} />
                              <span className="acm-overview__legend acm-overview__legend--medium">{item.label}</span>
                            </div>
                            <span className="acm-overview__caption">{pct}%</span>
                          </div>
                          <span className="acm-overview__metric-value acm-overview__metric-value--sm">{item.count.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="acm-overview__map-main">
                  <div className="acm-overview__widget-header">
                    <div className="acm-overview__region-row">
                      {regions.map((r) => {
                        const st = rStatus(r, C);
                        return (
                          <div key={r.id} className="acm-overview__region-pill">
                            <div style={{ width: 5, height: 5, borderRadius: '50%', background: st.color }} />
                            <span className="acm-overview__legend acm-overview__legend--medium">{r.id}</span>
                            <span className="acm-overview__caption">{rTotal(r).toLocaleString()}</span>
                          </div>
                        );
                      })}
                    </div>
                    <button type="button" className="acm-overview__icon-btn" aria-label="More options">
                      <MoreHorizontal size={14} />
                    </button>
                  </div>
                  <WorldMap C={C} />
                </div>
              </div>
            </OverviewCard>

            <OverviewCard className="acm-overview__span-3 acm-overview__card--security">
              <SecurityFeedWidget />
            </OverviewCard>
          </div>
        </div>
      </PageSection>
    </>
  );
};

export { ACMOpenShiftOverview };
